/**
 * <product-video-gallery>
 * Slider Swiper 11 de vídeos verticais (autoplay mudo + toggle de som).
 * Toggle de som replica o padrão de sections/ss-shoppable-video.liquid (home).
 * Barra de progresso customizada (verde Alvya) — não usa módulo scrollbar do Swiper.
 */
if (!customElements.get('product-video-gallery')) {
  customElements.define(
    'product-video-gallery',
    class ProductVideoGallery extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.slider = this.querySelector('.product-video-gallery__slider');
        this.scrollbarEl = this.querySelector('.product-video-gallery__scrollbar');
        this.scrollbarDrag = this.querySelector('.product-video-gallery__scrollbar-drag');
        this.slides = this.querySelectorAll('.product-video-gallery__slide');

        this.bindSoundToggle();
        this.observeAutoplay();
        this.loadSwiper().then(() => this.initSwiper());
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.swiper?.destroy(true, true);
      }

      bindSoundToggle() {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          const btn = slide.querySelector('[data-pvg-sound]');
          if (!video || !btn) return;

          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const turningOn = video.muted;

            this.querySelectorAll('.product-video-gallery__video').forEach((v) => {
              if (v !== video) v.muted = true;
            });
            this.querySelectorAll('[data-pvg-sound][aria-pressed="true"]').forEach((b) => {
              if (b !== btn) b.setAttribute('aria-pressed', 'false');
            });

            video.muted = !turningOn;
            btn.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
            btn.setAttribute('aria-label', turningOn ? 'Desativar som' : 'Ativar som');
          });
        });
      }

      observeAutoplay() {
        const videos = [...this.querySelectorAll('.product-video-gallery__video')];
        if (!videos.length || !('IntersectionObserver' in window)) return;

        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                const playPromise = video.play();
                playPromise?.catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );

        videos.forEach((video) => this.io.observe(video));
      }

      loadSwiper() {
        if (window.Swiper) return Promise.resolve();
        if (window.__pvgSwiperLoading) return window.__pvgSwiperLoading;

        const cssHref = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        if (!document.querySelector(`link[href="${cssHref}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssHref;
          document.head.appendChild(link);
        }

        window.__pvgSwiperLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });

        return window.__pvgSwiperLoading;
      }

      getSlidesPerView() {
        if (!this.swiper) return 1;
        const dynamic = this.swiper.slidesPerViewDynamic?.();
        if (typeof dynamic === 'number' && dynamic > 0) return dynamic;
        const configured = this.swiper.params.slidesPerView;
        return typeof configured === 'number' ? configured : 1;
      }

      getScrollbarColor() {
        const fromVar = getComputedStyle(this).getPropertyValue('--pvg-scrollbar-color').trim();
        return fromVar || '#a6cf54';
      }

      paintScrollbar() {
        if (!this.scrollbarEl || !this.scrollbarDrag) return;
        const color = this.getScrollbarColor();
        this.scrollbarEl.style.backgroundColor = 'rgba(18, 18, 18, 0.12)';
        this.scrollbarEl.style.height = '8px';
        this.scrollbarEl.style.minHeight = '8px';
        this.scrollbarEl.style.display = 'block';
        this.scrollbarEl.style.opacity = '1';
        this.scrollbarEl.style.visibility = 'visible';
        this.scrollbarDrag.style.backgroundColor = color;
        this.scrollbarDrag.style.height = '8px';
        this.scrollbarDrag.style.minHeight = '8px';
        this.scrollbarDrag.style.display = 'block';
        this.scrollbarDrag.style.opacity = '1';
        this.scrollbarDrag.style.visibility = 'visible';
      }

      updateScrollbar() {
        if (!this.swiper || !this.scrollbarDrag || !this.scrollbarEl) return;

        this.paintScrollbar();

        const slidesLength = this.swiper.slides.length;
        const perView = this.getSlidesPerView();
        const trackWidth = this.scrollbarEl.offsetWidth;

        if (!trackWidth || slidesLength <= perView) {
          this.scrollbarDrag.style.width = '100%';
          this.scrollbarDrag.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const dragWidthPx = (perView / slidesLength) * trackWidth;
        const maxTranslate = trackWidth - dragWidthPx;
        const translateX = this.swiper.progress * maxTranslate;

        this.scrollbarDrag.style.width = `${dragWidthPx}px`;
        this.scrollbarDrag.style.transform = `translate3d(${translateX}px, 0, 0)`;
      }

      initSwiper() {
        if (this.swiper || !window.Swiper) return;

        this.swiper = new window.Swiper(this.slider, {
          speed: 300,
          spaceBetween: 8,
          watchOverflow: true,
          mousewheel: {
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
          },
          breakpoints: {
            320: { spaceBetween: 8, slidesPerView: 2.5 },
            768: { spaceBetween: 8, slidesPerView: 2.5 },
            1024: { spaceBetween: 8, slidesPerView: 3 },
          },
          on: {
            init: () => this.updateScrollbar(),
            progress: () => this.updateScrollbar(),
            resize: () => this.updateScrollbar(),
            slideChange: () => this.updateScrollbar(),
            setTranslate: () => this.updateScrollbar(),
          },
        });

        window.requestAnimationFrame(() => this.updateScrollbar());
      }
    }
  );
}
